import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { alertError, alertSuccess } from '../../components/Toast';
import { sanitizeCnpj, isValidCnpj } from '../../core/utils/cnpj';
import { setData } from '../../core/redux/app/Actions';
import {
  deleteSupplier,
  findDuplicateSupplier,
  loadSupplierById,
  saveSupplier,
} from '../../core/utils/supplier';
import { validateEmail } from '../../core/utils/string';
import ModalSuppliers from '../../screens/ModalSuppliers/ModalSuppliers';

const INITIAL_VALUES = { status: 'ativo' };

const ModalSuppliersContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const id = location.pathname.split('/')[3];
  const isNew = params.id === '0';
  const reloadSupplier = useSelector((state) => state.app.reloadSupplier || false);
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});
  const [values, setValues] = useState(INITIAL_VALUES);
  const [oldValues, setOldValues] = useState(INITIAL_VALUES);

  const loadData = async () => {
    const supplier = await loadSupplierById(id);
    if (supplier) {
      setValues({ ...supplier });
      setOldValues({ ...supplier });
    }
  };

  useEffect(() => {
    if (id !== '0') {
      loadData();
    }
  }, [id]);

  useEffect(() => {
    const onBackButtonEvent = (e) => {
      e.preventDefault();
      closeModal();
    };
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []);

  const closeModal = () => {
    if (compareValues(oldValues, values)) {
      Swal.fire({
        title: 'Deseja sair sem salvar?',
        showCancelButton: true,
        showConfirmButton: false,
        showDenyButton: true,
        icon: 'warning',
        text: 'Ao sair, seus dados serão descartados.',
        denyButtonText: `Sair`,
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isDenied) {
          history.push('/suppliers');
        }
      });
    } else {
      history.push('/suppliers');
    }
  };

  const onChange = (field, value) => {
    setValues((values) => ({ ...values, [field]: value }));
  };

  const onSave = async () => {
    try {
      const supplier = { ...values };

      if (!supplier.name || !supplier.name.trim()) {
        alertError('Nome é obrigatório');
        return;
      }

      if (supplier.email && !validateEmail(supplier.email)) {
        alertError('E-mail inválido');
        return;
      }

      if (
        sanitizeCnpj(supplier.cpf_cnpj).length === 14 &&
        !isValidCnpj(supplier.cpf_cnpj)
      ) {
        alertError('CNPJ inválido');
        return;
      }

      // RN04/CA05: bloqueia duplicidade por Nome (+ CPF/CNPJ quando informado).
      const isDuplicate = await findDuplicateSupplier(
        company.id,
        supplier.name,
        supplier.cpf_cnpj,
        supplier.id
      );
      if (isDuplicate) {
        alertError('Já existe um fornecedor com este nome/documento.');
        return;
      }

      const { error } = await saveSupplier(
        supplier.id,
        {
          name: supplier.name.trim(),
          cpf_cnpj: supplier.cpf_cnpj || null,
          email: supplier.email || null,
          phone: supplier.phone || null,
          observation: supplier.observation || null,
          status: supplier.status || 'ativo',
          company_id: company.id,
        },
        user.id
      );

      if (error) {
        alertError('Erro ao salvar o fornecedor, tente novamente mais tarde.');
        return;
      }

      alertSuccess('Sucesso ao salvar o fornecedor!');
      history.push('/suppliers');
      dispatch(setData({ reloadSupplier: !reloadSupplier }));
    } catch (error) {
      alertError('Erro ao salvar o fornecedor, tente novamente mais tarde.');
    }
  };

  const onRemove = async () => {
    const confirmation = await Swal.fire({
      title: 'Deseja mesmo excluir?',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      icon: 'warning',
      text: 'O fornecedor será removido do cadastro.',
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });

    if (confirmation.isDenied) {
      if (values && values.id) {
        const { error } = await deleteSupplier(values.id, user.id);
        if (!error) {
          alertSuccess('Fornecedor removido com sucesso!');
          history.push('/suppliers');
          dispatch(setData({ reloadSupplier: !reloadSupplier }));
        } else {
          alertError('Erro ao remover o fornecedor, tente novamente mais tarde');
        }
      } else {
        alertError('Erro ao remover o fornecedor, tente novamente mais tarde');
      }
    } else {
      history.push('/suppliers');
    }
  };

  return (
    <ModalSuppliers
      compareValues={compareValues(oldValues, values)}
      onRemove={onRemove}
      closeModal={closeModal}
      onChange={onChange}
      onSave={onSave}
      values={values}
      isNew={isNew}
    />
  );
};

export default ModalSuppliersContainer;

export function compareValues(objA, objB) {
  if (objA && objB) {
    let aKey = Object.keys(objA),
      bKey = Object.keys(objB);

    if (aKey.length != bKey.length) {
      return true;
    }

    let areEqual = _.isEqual(objA, objB);

    return !areEqual;
  }
}
